import type { Point, SpatialRectangle } from "@pdfking/math"

type Edge<T extends number> = {
  inside: (p: Point<T>) => boolean
  cross: (a: Point<T>, b: Point<T>) => Point<T>
}

// The four sides of `box`, each keeping what lies on its side, and where a
// segment crosses it: on the side itself, exactly
const sides = <T extends number>({ x, y, width, height }: SpatialRectangle<T>): Edge<T>[] => {
  const at_x = (to: number) => (a: Point<T>, b: Point<T>) => ({
    x: to as T,
    y: (a.y + ((b.y - a.y) * (to - a.x)) / (b.x - a.x)) as T,
  })
  const at_y = (to: number) => (a: Point<T>, b: Point<T>) => ({
    x: (a.x + ((b.x - a.x) * (to - a.y)) / (b.y - a.y)) as T,
    y: to as T,
  })
  return [
    { inside: p => p.x >= x, cross: at_x(x) },
    { inside: p => p.x <= x + width, cross: at_x(x + width) },
    { inside: p => p.y >= y, cross: at_y(y) },
    { inside: p => p.y <= y + height, cross: at_y(y + height) },
  ]
}

/** The polygon `points` cut to `box` (Sutherland–Hodgman): a polygon of
 * fewer than three points when nothing of it falls within. A concave one cut
 * in two comes back as one, the halves joined along the box's side. */
export const ClipPolygon = <T extends number>(
  points: Point<T>[],
  box: SpatialRectangle<T>
): Point<T>[] =>
  sides(box).reduce(
    (polygon: Point<T>[], { inside, cross }) =>
      polygon.flatMap((to, i) => {
        const from = polygon[(i + polygon.length - 1) % polygon.length]!
        if (inside(to)) return inside(from) ? [to] : [cross(from, to), to]
        return inside(from) ? [cross(from, to)] : []
      }),
    [...points]
  )
